import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { designSystem } from '@yola/ws-ui';
import { customization, dialogs, view, i18next } from '@yola/ws-sdk';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import dialogCallSourceIds from 'src/js/modules/website-design/constants/dialog-call-source-ids';
import { INDETERMINATE } from '../constants';
import optionTypes from '../constants/option-types';
import ElementSettingsContext from '../helpers/element-settings-context';
import { getOptionFlatAttributesValues } from '../helpers/get-option-attributes-values';

const { ToggleSection, Box, ColorTileSelector } = designSystem;

function ColorSchemeOption({ option, settingId, onUpdateValue }) {
  const { state, submit, callbacks } = useContext(ElementSettingsContext);
  const { colors } = customization.accessors.getCurrentColorPalette();

  const attributesValues = getOptionFlatAttributesValues(state, option);
  const isToggleEnabled = attributesValues.some(Boolean);
  const isIndeterminate = !attributesValues.every((value) => value === attributesValues[0]);
  const currentValue = isIndeterminate ? INDETERMINATE : attributesValues[0];

  const paddingProps = {
    paddingTop: 'spacing-xs',
    paddingLeft: 'spacing-m',
    paddingRight: 'spacing-m',
    paddingBottom: isToggleEnabled ? 'spacing-s' : 'spacing-xs',
  };

  const updatedChoices = option.choices.map((choice) => {
    const colorValue = colors[choice.value];

    return {
      ...choice,
      backgroundColor: colorValue,
    };
  });

  const handleToggleChange = (val) => {
    if (val) {
      if (!currentValue) {
        const element = view.accessors.getLiveElement(option.elementIds[0]);
        const elementWithSurface = element.closest('[surfaces]');
        const [surface] = elementWithSurface.getAttribute('surfaces').split(' ');
        onUpdateValue(surface);
      }
    } else {
      onUpdateValue(false);
    }

    callbacks.onToggleChange({ optionId: option.id, settingId, isActive: val });
  };

  const handleColorSchemeChange = (value) => {
    onUpdateValue(value);
    callbacks.onOptionClicked({ optionId: option.id, settingId });
  };

  const showWebsiteDesignDialog = () => {
    submit();

    dialogs.operations.show(dialogTypes.DESIGN_SETTINGS_COLORS_DIALOG, {
      sourceId: dialogCallSourceIds.CUSTOMIZE_COLORS_FROM_SETTINGS,
    });
  };

  return (
    <Box {...paddingProps}>
      <ToggleSection
        value={isToggleEnabled}
        title={option.title}
        {...(option.popover && { popoverProps: option.popover })}
        onChange={handleToggleChange}
      >
        <ColorTileSelector
          value={currentValue}
          options={updatedChoices}
          indeterminateToolTipText={i18next.t('Mixed')}
          onChange={handleColorSchemeChange}
          onConfigureClick={showWebsiteDesignDialog}
        />
      </ToggleSection>
    </Box>
  );
}

ColorSchemeOption.propTypes = {
  option: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: optionTypes.COLOR_SCHEME,
    elementIds: PropTypes.arrayOf(PropTypes.string),
    attributes: PropTypes.arrayOf(PropTypes.string),
    choices: PropTypes.array,
    title: PropTypes.string,
    component: PropTypes.element,
    popover: PropTypes.shape({
      title: PropTypes.string.isRequired,
      imgSrc: PropTypes.string,
    }),
    computeFallbackValue: PropTypes.func,
  }).isRequired,
  settingId: PropTypes.string.isRequired,
  onUpdateValue: PropTypes.func.isRequired,
};

export default ColorSchemeOption;
